import {
  DEFAULT_INSIGHTS,
  DEFAULT_NOTIFICATIONS,
} from '~/constants/notifications'

import type { NuxtError } from '#app'
import type {
  AUTHENTICATION_INTENT,
  AUTHENTICATION_PROVIDERS,
  AUTHENTICATION_TYPE,
} from '~/constants/authentication'
import type {
  BookingChangeRequestParams,
  BookingChangeRequestResponse,
  BookingChangeRequestType,
  BookingEstimateParams,
  BookingExpandedDetails,
  HeaderSearchFormVisibility,
  Modal,
  Nullable,
  RvExpandedDetails,
  SearchData,
  SearchDates,
  SearchGuests,
  SearchLocation,
  SearchRentalType,
  VerificationData,
} from '~/types'
import type {
  AdditionalChargesPreviewResponse,
  BookingNotifications,
  BookingPriceResponse,
  Campground,
  DeliveryCostResponse,
  GetListProfile,
  Insights,
  NotificationIndicator,
  RvEntity,
  UpdateProfileParams,
  UserFavouritesResponse,
  UserPermissionsResponse,
} from '~/types/rental-api-aliases'

import type {
  SupportedBrowsers,
} from '#shared/types/browsers'

import type {
  GeolocationData,
} from '#shared/types/geolocation'

import type {
  VerifiedReviews,
} from '#shared/types/verified-reviews'

import type {
  Toast,
} from '~/components/_tailwind/toast/types'

import type {
  ExperimentsConfig,
} from '#shared/types/experiment'

import type {
  RentalAgreement,
} from '~/types/rental-agreement'
import type { EditRvScope } from '~/types/rv'

/**
 * A collection of SSR-safe states to be re-used throughout the app. This should
 * _not_ contain any local state (just use refs in components for that).
 */

// A user's favourites.
export const useFavourites = () => useState<UserFavouritesResponse>('favourites', () => [])

// A user's permissions.
export const usePermissions = () => useState<UserPermissionsResponse>('permissions', () => [])

// Wether or not the authentication form is visible.
export const useAuthenticationModal = () => useState('authenticationModal', () => false)

export const useAuthenticationType = () => useState<Nullable<AUTHENTICATION_TYPE>>('authenticationType', () => null)

export const useAuthenticationProvider = () =>
  useState<AUTHENTICATION_PROVIDERS | undefined>('authenticationProvider', () => undefined)

export const useAuthenticationIntent = () =>
  useState<AUTHENTICATION_INTENT | undefined>('authenticationIntent', () => undefined)

export const useProfileCompletionPending = () => useState('profileCompletionPending', () => false)

// Wether or not the header search form is visible.
export const useStickyHeaderSearchFormShown = () => useState('stickyHeaderSearchFormShown', () => true)

export const useRenderedHeaderSearchFormVisibility = () =>
  useState<HeaderSearchFormVisibility>('renderedHeaderSearchFormVisibility', () => 'visible')

// Wether or not the header search form is visible.
export const useShowSearchMapMobile = () => useState('showSearchMapMobile', () => false)

// Stores a user's geolocation data.
export const useGeolocationData = () => useState<Nullable<GeolocationData>>('geolocation', () => null)

// Stores the hydration state of the app.
export const useHydrationStatus = () => useState('isHydrated', () => false)

// Experiments.
export const useExperimentsConfig = () => useState<ExperimentsConfig | undefined>('experimentsConfig', () => undefined)

// Ask owner question modal.
export const useShowOwnerQuestionModal = () => useState<boolean>('showOwnerQuestionModal', () => false)

// Currently selected RV.
export const useSelectedRvDetails = () => useState<Nullable<RvExpandedDetails>>('selectedRvDetails', () => null)

// Booking Price.
export const useBookingEstimateParams = () => useState<BookingEstimateParams>('bookingEstimateParams', () => ({
  dateEnd: '',
  dateStart: '',
  protectionLevel: null,
  roadsideAssistance: true,
  adults: 1,
  children: 0,
  pets: false,
  rvUsage: null,
  addons: [],
  destination: undefined,
}))

// State used to store the PII payload from the RTB flow fields for first name, last name and phone number
// https://rvezy.atlassian.net/browse/RVZ-22365
export const useBookingPIIParams = () => useState<Nullable<UpdateProfileParams>>('bookingPIIParams', () => null)

// Set while the insured delivery follow-up (Delivery vs StationaryAfterDelivery) is unanswered, so the destination stays incomplete after the picker closes
export const useHasPendingDeliveryType = () => useState<boolean>('hasPendingDeliveryType', () => false)

export const useBookingEstimatePending = () => useState('bookingEstimatePending', () => false)
export const useBookingEstimateError = () => useState<Nullable<NuxtError>>('bookingEstimateError', () => null)
export const useBookingEstimateData = () => useState<Nullable<BookingPriceResponse>>('bookingEstimateData', () => null)

export const useDeliveryCostPending = () => useState<boolean>('deliveryCostPending', () => false)
export const useDeliveryCostData = () => useState<Nullable<DeliveryCostResponse>>('deliveryCostData', () => null)
export const useDeliveryCostError = () => useState<Nullable<NuxtError>>('deliveryCostError', () => null)

export const useSelectedBookingDetails = () => useState<Nullable<BookingExpandedDetails>>('selectedBookingDetails', () => null)
export const useSelectedBookingNotifications = () => useState<Nullable<BookingNotifications>>('selectedBookingNotifications', () => [])
export const useSelectedBookingUsers = () => useState<Nullable<Record<'owner' | 'renter', Nullable<GetListProfile>>>>('selectedBookingUsers', () => null)

export const usePromoCodePending = () => useState<boolean>('promoCodePending', () => false)
export const usePromoCodeError = () => useState<Nullable<NuxtError>>('promoCodeError', () => null)
export const usePromoCodeData = () => useState<Nullable<string>>('promoCode', () => '')

// Global toasts queue.
export const useToasts = () => useState<Toast[]>('toasts', () => [])

export const useModalsQueue = () => useState<{
  queue: Modal[]
  index: number
  isVisible: boolean
}>('modalsQueue', () => ({
  queue: [],
  index: -1,
  isVisible: false,
}))

export const useChangeRequestType = () => useState<Nullable<BookingChangeRequestType>>('changeRequestType', () => null)
export const useChangeRequestParams = () => useState<Nullable<Partial<BookingChangeRequestParams>>>('changeRequestParams', () => null)
export const useChangeRequestPending = () => useState<boolean>('changeRequestPending', () => false)
export const useChangeRequestError = () => useState<Nullable<NuxtError>>('changeRequestError', () => null)
export const useChangeRequestSuccess = () => useState<boolean>('changeRequestSuccess', () => false)
export const useSelectedChangeRequestData = () => useState<Nullable<BookingChangeRequestResponse>>('changeRequestData', () => null)

export const useAdditionalChargesResponse = () => useState<Nullable<AdditionalChargesPreviewResponse>>('additionalCharges', () => null)

export const useRentalAgreement = () => useState<RentalAgreement>('rentalAgreement', () => ({
  items: [],
  vouched: undefined,
}))

export const useEditRvData = (scope: EditRvScope = 'edit') => useState<Nullable<RvEntity>>(`editRvData:${scope}`, () => null)
export const useEditRvParams = (scope: EditRvScope = 'edit') => useState<Nullable<Partial<RvEntity>>>(`editRvParams:${scope}`, () => null)

export const useEditCampgroundData = () => useState<Nullable<Campground>>('editCampgroundData', () => null)
export const useEditCampgroundParams = () => useState<Nullable<Partial<Campground>>>('editCampgroundParams', () => null)

export const useVerifiedReviews = () => useState<Nullable<VerifiedReviews>>('verifiedReviews', () => null)

export const useEnquiryOwnerName = () => useState<string | undefined>('enquiryOwnerName', () => undefined)

export const useIsExternalAuthenticationLoading = () => useState('externalAuthenticationLoading', () => false)

export type ExternalAuthError = Nullable<{ code?: string }>
export const useExternalAuthError = () => useState<ExternalAuthError>('externalAuthError', () => null)

export const useCanAddDriver = () => useState('canAddDriver', () => false)

export const useVerificationData = () => useState<Nullable<VerificationData>>('verificationData', () => null)

export const useSupportedBrowsers = () => useState<Nullable<SupportedBrowsers>>('supportedBrowsers', () => null)

export const useRenderedRoute = () => useState('renderedRoute', () => 'index')

export const useModalStackIds = () => useState<string[]>('modalStackIds', () => [])

export const useSearchData = () => useState<SearchData>('searchData', createDefaultSearchData)

export const useIsMenuFilterApplied = () => useState('isMenuFilterApplied', () => false)

export const useSearchFormData = () => useState<{
  dates: SearchDates | undefined
  guests: SearchGuests | undefined
  location: SearchLocation | undefined
  rentalType: SearchRentalType | undefined
}>('searchFormData', () => ({
  dates: undefined,
  guests: undefined,
  location: undefined,
  rentalType: undefined,
}))

export const useNotifications = () => useState<NotificationIndicator>('notifications', () => (DEFAULT_NOTIFICATIONS))

export const useInsights = () => useState<Insights>('insights', () => (DEFAULT_INSIGHTS))

export const useIsDraftReady = () => useState('request-to-book-draft-ready', () => false)
